import { DialogModal } from '../../components';
import Breadcrumbs from '../../components/Breadcrumb';
import devices from '../../components/canvas-editor-v2/assets/devices.json';
import { DEBOUNCE_TIME } from '../../configs';
import { UserRole } from '../../types/user.types';
import { metaTitle } from '../../util/tabTitle';
import { useIsUserRole } from '../../util/useIsUserRole';
import { SlideForm } from './SlideForm';
import SlideTable from './SlidesTable';
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  Menu,
  Select,
  MenuItem,
  debounce,
  SelectChangeEvent,
  Stack,
} from '@mui/material';
import { ViewColumnOutlined } from '@mui/icons-material';
import { FC, useState } from 'react';
import {
  ManagementPageHeader,
  ManagementSearch,
  ManagementToolbar,
  RefreshAction,
} from '../../components/ManagementPageLayout';
import { useHandleSearch } from '../../util/useHandleSearch';
import { useLocationSearch } from '../../util/useLocationSearch';

export interface SlideColumns {
  createdBy: boolean;
  created: boolean;
}

const SlidesPage: FC = () => {
  metaTitle('Slides');

  const searchVal = useLocationSearch();

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState(searchVal || '');
  const [searchedSlide, setSearchedSlide] = useState(searchVal || '');
  const [selectedDevice, setSelectedDevice] = useState('');
  const [refresh, setRefresh] = useState(false);
  const [columnAnchor, setColumnAnchor] = useState<null | HTMLElement>(null);
  const [visibleColumns, setVisibleColumns] = useState<SlideColumns>({
    createdBy: false,
    created: false,
  });

  useHandleSearch(search);

  const isViewOnly = useIsUserRole([UserRole.user, UserRole.dataEditor]);

  const searchSlides = (searchTerm: string) => {
    setSearchedSlide(searchTerm);
  };

  const handleDeviceChange = (
    event: SelectChangeEvent<typeof selectedDevice>,
  ) => {
    setSelectedDevice(event.target.value);
  };

  return (
    <Box sx={{ width: '100%', maxWidth: 1360, mx: 'auto' }}>
      <Breadcrumbs
        items={[
          {
            label: 'Slides',
            to: '/slides',
            description:
              'This page houses all created slides and enables users to create and customize slides for presentations. Users can choose from various components such as images, videos, text, shapes, KVS values, and widgets. They can create and edit slides multiple times within a presentation.Slides can be filtered by screen type and searched by name. Available actions include renaming slides, cloning (creating copies), and removing slides.',
          },
        ]}
      />

      <ManagementPageHeader
        title="Slides"
        subtitle="Create and manage reusable content for your presentations."
        actionLabel="New slide"
        onAction={() => setOpen(true)}
        actionDisabled={isViewOnly}
      />
      <ManagementToolbar>
        <ManagementSearch
          placeholder="Search slides"
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            debounce(() => searchSlides(event.target.value), DEBOUNCE_TIME)();
          }}
        />
        <Stack direction="row" spacing={1.25}>
          <FormControl size="small" sx={{ minWidth: { xs: 200, sm: 260 } }}>
            <Select
              value={selectedDevice}
              displayEmpty
              inputProps={{ 'aria-label': 'Filter by screen size' }}
              onChange={handleDeviceChange}
              sx={{ height: 40, borderRadius: 2 }}
            >
              <MenuItem value="">All screen sizes</MenuItem>
              {devices?.map((device) => (
                <MenuItem key={device.id} value={device.id}>
                  {device.name} ({device.type})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            startIcon={<ViewColumnOutlined />}
            onClick={(event) => setColumnAnchor(event.currentTarget)}
            sx={{
              height: 40,
              borderRadius: 2,
              textTransform: 'none',
              color: '#334155',
              borderColor: '#cbd5e1',
            }}
          >
            Columns
          </Button>
          <Menu
            anchorEl={columnAnchor}
            open={Boolean(columnAnchor)}
            onClose={() => setColumnAnchor(null)}
          >
            <MenuItem
              onClick={() => {
                setVisibleColumns((value) => ({
                  ...value,
                  createdBy: !value.createdBy,
                }));
                setColumnAnchor(null);
              }}
            >
              <Checkbox checked={visibleColumns.createdBy} size="small" />
              Created by
            </MenuItem>
            <MenuItem
              onClick={() => {
                setVisibleColumns((value) => ({
                  ...value,
                  created: !value.created,
                }));
                setColumnAnchor(null);
              }}
            >
              <Checkbox checked={visibleColumns.created} size="small" />
              Created
            </MenuItem>
          </Menu>
          <RefreshAction onClick={() => setRefresh(!refresh)} />
        </Stack>
      </ManagementToolbar>

      <SlideTable
        searchedSlide={searchedSlide}
        selectedDevice={selectedDevice}
        onRefresh={refresh}
        visibleColumns={visibleColumns}
      />

      <DialogModal title="New slide" onClose={() => setOpen(false)} open={open}>
        <SlideForm onFinished={() => setOpen(false)} />
      </DialogModal>
    </Box>
  );
};

export default SlidesPage;
